import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import ReportModal from './components/ReportModal';
import VoteButtons from './components/VoteButtons';
import { useOSMMosques } from './hooks/useOSMMosques';
import AddMosqueModal from './components/AddMosqueModal';
import MosqueListSheet from './components/MosqueListSheet';

const mosqueIcon = (hasBiryani, score, isDark) => {
  const color = hasBiryani === null ? '#9ca3af' : hasBiryani ? '#10b981' : '#ef4444';
  const bgColor = isDark ? '#0f0f0f' : '#ffffff';
  const glow = hasBiryani ? '0 0 15px rgba(16, 185, 129, 0.5), 0 0 25px rgba(16, 185, 129, 0.3)' : hasBiryani === null ? 'none' : '0 0 10px rgba(239, 68, 68, 0.3)';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center;">
        ${hasBiryani !== null ? `
          <div style="
            position: absolute;
            top: -8px;
            right: -8px;
            background: ${hasBiryani ? '#10b981' : '#ef4444'};
            color: white;
            font-size: 9px;
            font-weight: 700;
            padding: 2px 6px;
            border-radius: 10px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          ">${score > 0 ? '+' : ''}${score}</div>
        ` : ''}
        <div style="
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: ${bgColor};
          border: 2px solid ${color};
          box-shadow: ${glow};
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <span style="font-size: 24px;">🕌</span>
        </div>
      </div>
    `,
    iconSize: [48, 60],
    iconAnchor: [24, 60]
  });
};

function getTileId(lat, lng, zoom = 14) {
  const n = Math.pow(2, zoom);
  const x = Math.floor((lng + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return `tile:${zoom}:${x}:${y}`;
}

function MapController({ onMoveEnd, userLocation, onMapDoubleClick }) {
  const map = useMap();
  const hasCenteredOnUserRef = useRef(false);

  useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      const tileId = getTileId(center.lat, center.lng, 14);
      onMoveEnd({ center, tileId, zoom: map.getZoom() });
    },
    // Double-click on map → add mosque at that location
    dblclick: (e) => {
      e.originalEvent.preventDefault();
      onMapDoubleClick && onMapDoubleClick(e.latlng);
    },
  });

  // BUG-001 FIX: userLocation পেলে map সেখানে flyTo করো
  useEffect(() => {
    if (!userLocation) return;
    if (hasCenteredOnUserRef.current) return;
    hasCenteredOnUserRef.current = true;
    map.flyTo([userLocation.lat, userLocation.lng], 15, { animate: true, duration: 1.2 });
  }, [userLocation, map]);

  return null;
}

// User's current location marker — blue pulsing dot
function UserLocationMarker({ userLocation }) {
  if (!userLocation) return null;

  const icon = L.divIcon({
    className: '',
    html: `
      <div style="position:relative;display:flex;align-items:center;justify-content:center;width:24px;height:24px;">
        <div style="
          position:absolute;
          width:24px;height:24px;
          border-radius:50%;
          background:rgba(59,130,246,0.25);
          animation:user-pulse 2s ease-out infinite;
        "></div>
        <div style="
          width:14px;height:14px;
          border-radius:50%;
          background:#3b82f6;
          border:2.5px solid white;
          box-shadow:0 2px 8px rgba(59,130,246,0.6);
          position:relative;z-index:1;
        "></div>
      </div>
      <style>
        @keyframes user-pulse {
          0%   { transform:scale(1); opacity:0.8; }
          70%  { transform:scale(2.5); opacity:0; }
          100% { transform:scale(1); opacity:0; }
        }
      </style>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  return (
    <Marker position={[userLocation.lat, userLocation.lng]} icon={icon} zIndexOffset={1000}>
      <Popup>
        <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 13, fontWeight: 600 }}>
          📍 আপনি এখানে আছেন
        </div>
      </Popup>
    </Marker>
  );
}

function MosqueAutoFit({ mosques, userLocation }) {
  const map = useMap();
  const hasFittedRef = useRef(false);

  // BUG-002 FIX: userLocation থাকলে সব মসজিদে fitBounds না করে
  // user এর location এ focus করো। Location না থাকলে আগের মতো fitBounds।
  useEffect(() => {
    if (!mosques || mosques.length === 0) return;
    if (hasFittedRef.current) return;
    // userLocation থাকলে MapController flyTo handle করবে, এখানে কিছু করার নেই
    if (userLocation) {
      hasFittedRef.current = true;
      return;
    }
    // location নেই — সব মসজিদ দেখাও
    const bounds = L.latLngBounds(mosques.map((m) => [m.lat, m.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
      hasFittedRef.current = true;
    }
  }, [map, mosques, userLocation]);

  return null;
}

const fallbackMosques = [
  { id: 'mosque_001', name: 'বাটা সৈয়দ শাহ মসজিদ', lat: 23.8103, lng: 90.4125, address: 'বাটা, ঢাকা', today: null },
  { id: 'mosque_002', name: 'লালবাগ মসজিদ', lat: 23.7206, lng: 90.4334, address: 'লালবাগ, ঢাকা', today: null },
  { id: 'mosque_003', name: 'খানে মোহাম্মদ আলী মসজিদ', lat: 23.7367, lng: 90.3985, address: 'মোহাম্মদপুর, ঢাকা', today: null },
  { id: 'mosque_004', name: 'তুরাগ মসজিদ', lat: 23.7854, lng: 90.3421, address: 'তুরাগ, ঢাকা', today: null },
  { id: 'mosque_005', name: 'বারিধারা মসজিদ', lat: 23.7945, lng: 90.4056, address: 'বারিধারা, ঢাকা', today: null },
  { id: 'mosque_006', name: 'গুলশান মসজিদ', lat: 23.7985, lng: 90.4012, address: 'গুলশান, ঢাকা', today: null },
  { id: 'mosque_007', name: 'বনানী মসজিদ', lat: 23.7654, lng: 90.3945, address: 'বনানী, ঢাকা', today: null },
  { id: 'mosque_008', name: 'মিরপুর মসজিদ', lat: 23.8012, lng: 90.3567, address: 'মিরপুর, ঢাকা', today: null },
  { id: 'mosque_009', name: 'উত্তরা মসজিদ', lat: 23.8654, lng: 90.4012, address: 'উত্তরা, ঢাকা', today: null },
  { id: 'mosque_010', name: 'ধামরাই মসজিদ', lat: 23.9123, lng: 90.4567, address: 'ধামরাই, ঢাকা', today: null }
];

function MosqueMarker({ mosque, onVote, isDark, onSelect }) {
  const [showPopup, setShowPopup] = useState(false);
  
  const today = mosque.today;
  const hasReport = today && today.report_id;
  const hasBiryani = hasReport ? today.score > 0 : null;
  
  return (
    <Marker
      position={[mosque.lat, mosque.lng]} 
      icon={mosqueIcon(hasBiryani, today?.score || 0, isDark)}
      eventHandlers={{
        click: () => {
          setShowPopup(!showPopup);
          onSelect && onSelect(mosque);
        }
      }}
    >
      {showPopup && (
        <Popup className="custom-popup">
          <div style={{ minWidth: 220, padding: '8px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <h4 style={{ marginBottom: '8px', fontSize: '16px', fontWeight: 700, color: isDark ? '#ffffff' : '#1f2937' }}>{mosque.name}</h4>
            
            {hasReport ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '28px' }}>{today.score > 0 ? '✓' : '✗'}</span>
                  <span style={{ color: today.score > 0 ? '#10b981' : '#ef4444', fontWeight: 600, fontSize: '14px' }}>
                    {today.score > 0 ? 'আজ বিরিয়ানি আছে' : 'আজ বিরিয়ানি নেই'}
                  </span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: isDark ? '#1f2937' : '#f3f4f6', borderRadius: '12px', marginBottom: '12px' }}>
                  <span style={{ color: '#10b981', fontWeight: 700 }}>✓ {today.yes_count}</span>
                  <span style={{ color: '#ef4444', fontWeight: 700 }}>✗ {today.no_count}</span>
                </div>
                
                <VoteButtons reportId={today.report_id} yesCount={today.yes_count} noCount={today.no_count} onVote={onVote} />
              </>
            ) : (
              <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '12px' }}>আজকের জন্য কোনো রিপোর্ট নেই</p>
            )}
            
            <button 
              onClick={() => window.openReportModal && window.openReportModal(mosque)}
              style={{
                width: '100%',
                padding: '10px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
            >
              + রিপোর্ট করুন
            </button>
          </div>
        </Popup>
      )}
    </Marker>
  );
}

function DesktopSidebar({ mosques, selectedMosque, onSelectMosque, isDark, onToggleTheme }) {
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const subTextColor = isDark ? '#94a3b8' : '#64748b';
  const cardBg = isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)';
  const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)';
  
  return (
    <div style={{
      width: 380,
      height: '100vh',
      background: cardBg,
      borderRight: cardBorder,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <div style={{ padding: 24, borderBottom: cardBorder }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)'
            }}>
              <span style={{ fontSize: 24 }}>🕌</span>
            </div>
            <div>
              <h1 style={{ color: textColor, fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>বিরিয়ানি দিবে</h1>
              <span style={{ color: '#10b981', fontSize: 11, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>Live Tracker</span>
            </div>
          </div>
          <button 
            onClick={onToggleTheme}
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20
            }}
          >
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, padding: '8px 12px', borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
          <span style={{ color: '#10b981', fontSize: 14 }}>📍</span>
          <span style={{ fontSize: 12, color: subTextColor }}>
            <span style={{ fontWeight: 600, color: textColor }}>DigitalCare.site</span> তৈরি করেছে
          </span>
        </div>
      </div>
      
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 0' }}>
        <div style={{ padding: '0 24px', marginBottom: 12 }}>
          <h2 style={{ color: textColor, fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>আজকের মসজিদ</h2>
          <p style={{ color: subTextColor, fontSize: 13 }}>{mosques.length} টি মসজিদ • সক্রিয়</p>
        </div>
        
        {mosques.map(mosque => {
          const today = mosque.today;
          const hasReport = today && today.report_id;
          const isSelected = selectedMosque && selectedMosque.id === mosque.id;
          
          return (
            <div 
              key={mosque.id}
              onClick={() => onSelectMosque(mosque)}
              style={{
                padding: '16px 24px',
                cursor: 'pointer',
                background: isSelected ? (isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)') : 'transparent',
                borderLeft: isSelected ? '3px solid #10b981' : '3px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h3 style={{ color: textColor, fontSize: 15, fontWeight: 600 }}>{mosque.name}</h3>
                {hasReport && (
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 700,
                    background: today.score > 0 ? '#10b981' : '#ef4444',
                    color: 'white'
                  }}>
                    {today.score > 0 ? '✓ আছে' : '✗ নেই'}
                  </span>
                )}
              </div>
              
              {hasReport && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>✓ {today.yes_count}</span>
                  <span style={{ color: '#ef4444', fontSize: 13, fontWeight: 600 }}>✗ {today.no_count}</span>
                  <span style={{ color: subTextColor, fontSize: 12 }}>Score: <span style={{ fontWeight: 600, color: today.score > 0 ? '#10b981' : '#ef4444' }}>{today.score > 0 ? '+' : ''}{today.score}</span></span>
                </div>
              )}
              
              {!hasReport && (
                <p style={{ color: subTextColor, fontSize: 12 }}>আজকের জন্য কোনো রিপোর্ট নেই</p>
              )}
            </div>
          );
        })}
      </div>
      
      <div style={{ padding: 20, borderTop: cardBorder, background: isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(248, 250, 252, 0.95)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 300, letterSpacing: '2px', textTransform: 'uppercase' }}>Sponsored by</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: textColor }}>Ozzyl.com</span>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [feedMosques, setFeedMosques] = useState([]); // D1 / static feed data
  const [loading, setLoading] = useState(true);
  const [feedError, setFeedError] = useState(null);
  const [feedDate, setFeedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentTile, setCurrentTile] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedMosque, setSelectedMosque] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fastRefreshUntil, setFastRefreshUntil] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [showAddMosqueModal, setShowAddMosqueModal] = useState(false);
  const [addMosqueLocation, setAddMosqueLocation] = useState(null); // { lat, lng }
  const [showListSheet, setShowListSheet] = useState(false);
  const fastRefreshResetTimerRef = useRef(null);
  const moveFetchDebounceRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  // OSM Overpass — auto-fetch real mosques near user location
  const { mosques: osmMosques, loading: osmLoading } = useOSMMosques(userLocation, 3000);

  // Merge OSM mosques with D1 feed data.
  // D1 feed has today's biryani report info — OSM has real mosque locations.
  // Strategy:
  //   1. Start with OSM mosques (real, auto-discovered)
  //   2. Overlay D1 report data by PROXIMITY (50m radius) — not just ID match
  //      because D1 seeded mosques have different IDs than OSM nodes
  //   3. Add any D1 mosques that are NOT within 50m of any OSM mosque
  const mosques = useMemo(() => {
    if (osmMosques.length === 0) {
      // No OSM data yet — use D1 feed directly (fallback)
      return feedMosques;
    }

    // ~50 metres in degrees (roughly 0.00045°)
    const PROXIMITY_DEG = 0.00045;

    const isNear = (a, b) =>
      Math.abs(a.lat - b.lat) < PROXIMITY_DEG &&
      Math.abs(a.lng - b.lng) < PROXIMITY_DEG;

    // Find the closest D1 mosque to each OSM mosque (within 50m)
    const merged = osmMosques.map((osm) => {
      const match = feedMosques.find((f) => isNear(osm, f));
      return {
        ...osm,
        today: match?.today ?? null,
      };
    });

    // Add D1 mosques that have NO nearby OSM equivalent (user-added / seeded only)
    for (const f of feedMosques) {
      const alreadyCovered = merged.some((osm) => isNear(osm, f));
      if (!alreadyCovered) {
        merged.push(f);
      }
    }

    return merged;
  }, [osmMosques, feedMosques]);
  
  const today = new Date().toISOString().split('T')[0];
  
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (!siteKey) return;

    const containerId = 'turnstile-widget-root';
    const ensureWidget = () => {
      if (!window.turnstile) return;
      if (turnstileWidgetIdRef.current !== null) return;
      turnstileWidgetIdRef.current = window.turnstile.render(`#${containerId}`, {
        sitekey: siteKey,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken('')
      });
    };

    if (!document.getElementById('cf-turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'cf-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = ensureWidget;
      document.head.appendChild(script);
    } else {
      ensureWidget();
    }
  }, []);
  
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);
  
  const fetchFeed = useCallback(async (tileId, options = {}) => {
    const { silent = false, bust = false } = options;
    try {
      if (!silent) setLoading(true);
      const todayDate = new Date().toISOString().split('T')[0];
      const dateCandidates = Array.from(new Set([feedDate, todayDate]));
      const cacheBust = bust ? `?v=${Math.floor(Date.now() / 20000)}` : '';
      let data = null;
      let securityBlocked = false;

      for (const date of dateCandidates) {
        const tileFile = tileId ? `${tileId.replace(/:/g, '_')}.json` : 'all.json';
        const tileUrl = `/data/${date}/${tileFile}${cacheBust}`;
        const tileRes = await fetch(tileUrl);

        if (tileRes.status === 403) {
          securityBlocked = true;
          continue;
        }

        if (tileRes.ok) {
          data = await tileRes.json();
          setFeedDate(date);
          break;
        }

        if (!tileId) continue;

        const fallbackRes = await fetch(`/data/${date}/all.json${cacheBust}`);
        if (fallbackRes.status === 403) {
          securityBlocked = true;
          continue;
        }
        if (fallbackRes.ok) {
          data = await fallbackRes.json();
          setFeedDate(date);
          break;
        }
      }

      if (!data) {
        for (const date of dateCandidates) {
          const liveRes = await fetch(`/api/feed?date=${date}${tileId ? `&area=${encodeURIComponent(tileId)}` : ''}`);
          if (liveRes.status === 403) {
            securityBlocked = true;
            continue;
          }
          if (liveRes.ok) {
            data = await liveRes.json();
            setFeedDate(date);
            setFeedError('Static cache blocked, live API mode এ ডাটা লোড হচ্ছে।');
            break;
          }
        }
      }

      if (!data) {
        if (securityBlocked) {
          throw new Error('SECURITY_CHALLENGE_BLOCK');
        }
        throw new Error('Failed to fetch static feed');
      }

      if (data.mosques && data.mosques.length > 0) {
        setFeedMosques(data.mosques);
        setFeedError(null);
      } else {
        setFeedMosques(fallbackMosques);
        setFeedError('লাইভ ডেটা এখনো পাওয়া যায়নি। fallback mosque list দেখানো হচ্ছে।');
      }
    } catch (err) {
      console.error('Feed error:', err);
      setFeedMosques(fallbackMosques);
      if (String(err?.message || '').includes('SECURITY_CHALLENGE_BLOCK')) {
        setFeedError('ডোমেইন সিকিউরিটি চ্যালেঞ্জের কারণে লাইভ ডেটা ব্লক হচ্ছে। fallback mosque list দেখানো হচ্ছে।');
      } else {
        setFeedError('ডেটা লোড করতে সমস্যা হচ্ছে। fallback mosque list দেখানো হচ্ছে।');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [feedDate]);
  
  useEffect(() => {
    fetchFeed(null);
  }, [fetchFeed]);

  const triggerFastRefreshWindow = useCallback(() => {
    const until = Date.now() + (5 * 60 * 1000);
    setFastRefreshUntil(until);
    if (fastRefreshResetTimerRef.current) {
      clearTimeout(fastRefreshResetTimerRef.current);
    }
    fastRefreshResetTimerRef.current = setTimeout(() => {
      setFastRefreshUntil(0);
      fastRefreshResetTimerRef.current = null;
    }, (5 * 60 * 1000) + 250);
    fetchFeed(currentTile, { silent: true, bust: true });
  }, [currentTile, fetchFeed]);

  useEffect(() => () => {
    if (fastRefreshResetTimerRef.current) {
      clearTimeout(fastRefreshResetTimerRef.current);
    }
    if (moveFetchDebounceRef.current) {
      clearTimeout(moveFetchDebounceRef.current);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchFeed(currentTile, { silent: true });
      }
    }, 120000);
    return () => clearInterval(interval);
  }, [currentTile, fetchFeed]);

  useEffect(() => {
    if (fastRefreshUntil <= Date.now()) return undefined;

    const interval = setInterval(() => {
      if (Date.now() > fastRefreshUntil) return;
      if (!document.hidden) {
        fetchFeed(currentTile, { silent: true, bust: true });
      }
    }, 20000);

    return () => clearInterval(interval);
  }, [currentTile, fastRefreshUntil, fetchFeed]);
  
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Location not supported');
      return;
    }
    
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        let message = 'Location access denied';
        if (error.code === 1) message = 'Location permission denied';
        else if (error.code === 2) message = 'Location unavailable';
        else if (error.code === 3) message = 'Location timeout';
        setLocationError(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);
  
  useEffect(() => {
    requestLocation();
  }, [requestLocation]);
  
  const handleMapMove = ({ tileId }) => {
    if (tileId !== currentTile) {
      setCurrentTile(tileId);
      if (moveFetchDebounceRef.current) {
        clearTimeout(moveFetchDebounceRef.current);
      }
      moveFetchDebounceRef.current = setTimeout(() => {
        fetchFeed(tileId, { silent: true });
        moveFetchDebounceRef.current = null;
      }, 250);
    }
  };

  const applyOptimisticVote = useCallback((reportId, vote) => {
    // Update feedMosques — the merged `mosques` will re-compute via useMemo
    setFeedMosques((prev) => prev.map((mosque) => {
      const todayInfo = mosque.today;
      if (!todayInfo || todayInfo.report_id !== reportId) return mosque;

      const nextYes = (todayInfo.yes_count || 0) + (vote === 1 ? 1 : 0);
      const nextNo = (todayInfo.no_count || 0) + (vote === -1 ? 1 : 0);
      const nextScore = (todayInfo.score || 0) + vote;
      const total = nextYes + nextNo;
      const nextConfidence = total > 0 ? nextYes / total : 0;

      return {
        ...mosque,
        today: {
          ...todayInfo,
          yes_count: nextYes,
          no_count: nextNo,
          score: nextScore,
          confidence: nextConfidence
        }
      };
    }));
  }, []);

  const applyOptimisticReport = useCallback((mosqueId) => {
    setFeedMosques((prev) => prev.map((mosque) => {
      if (mosque.id !== mosqueId || mosque.today?.report_id) return mosque;
      return {
        ...mosque,
        today: {
          report_id: `pending-${Date.now()}`,
          has_biryani: true,
          yes_count: 1,
          no_count: 0,
          score: 1,
          confidence: 1,
          status: 'active'
        }
      };
    }));
  }, []);
  
  const handleVote = async (reportId, vote) => {
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !turnstileToken) {
      setFeedError('অনুগ্রহ করে নিরাপত্তা যাচাই সম্পন্ন করুন, তারপর ভোট দিন।');
      return;
    }
    applyOptimisticVote(reportId, vote);
    try {
      const res = await fetch('/api/vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          votes: [{ report_id: reportId, vote }],
          turnstileToken: turnstileToken || undefined
        })
      });
      if (res.ok) {
        triggerFastRefreshWindow();
      } else {
        fetchFeed(currentTile, { silent: true, bust: true });
      }
    } catch (err) {
      console.error('Vote error:', err);
      fetchFeed(currentTile, { silent: true, bust: true });
    }
  };
  
  const handleReport = (mosque) => {
    setSelectedMosque(mosque);
    setShowReportModal(true);
  };
  
  const handleReportSubmit = async (data) => {
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !turnstileToken) {
      setFeedError('অনুগ্রহ করে নিরাপত্তা যাচাই সম্পন্ন করুন, তারপর রিপোর্ট দিন।');
      return;
    }
    applyOptimisticReport(data.mosque_id);
    try {
      const res = await fetch('/api/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          date: today,
          turnstileToken: turnstileToken || undefined
        })
      });
      if (res.ok) {
        setShowReportModal(false);
        triggerFastRefreshWindow();
      } else {
        fetchFeed(currentTile, { silent: true, bust: true });
      }
    } catch (err) {
      console.error('Report error:', err);
      fetchFeed(currentTile, { silent: true, bust: true });
    }
  };
  
  const handleSelectMosque = (mosque) => {
    setSelectedMosque(mosque);
  };

  // Map long-press / double-click → open AddMosqueModal at that location
  const handleMapClick = useCallback((latlng) => {
    setAddMosqueLocation({ lat: latlng.lat, lng: latlng.lng });
    setShowAddMosqueModal(true);
  }, []);

  const handleAddMosqueSubmit = async ({ name, lat, lng, address }) => {
    const res = await fetch('/api/mosque', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, lat, lng, address }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to add mosque');
    }
    // Optimistically add to feedMosques so it shows on map immediately
    setFeedMosques((prev) => [
      ...prev,
      { id: data.mosque_id, name, lat, lng, address, today: null },
    ]);
    setShowAddMosqueModal(false);
    setAddMosqueLocation(null);
  };

  window.openReportModal = handleReport;
  
  const bgColor = isDark ? '#050505' : '#f8fafc';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const glassPanel = isDark ? 'rgba(15, 25, 20, 0.7)' : 'rgba(255, 255, 255, 0.85)';
  const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)';
  
  if (initialLoading) {
    return (
      <div style={{ 
        height: '100vh', 
        width: '100vw', 
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20
      }}>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div style={{
          width: 80,
          height: 80,
          borderRadius: 24,
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }}>
          <span style={{ fontSize: 40 }}>🕌</span>
        </div>
        <h1 style={{ color: 'white', fontSize: 28, fontWeight: 800, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>বিরিয়ানি দিবে</h1>
        <div style={{ 
          width: 40, 
          height: 40, 
          border: '3px solid rgba(255,255,255,0.3)', 
          borderTopColor: 'white', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  
  if (isDesktop) {
    return (
      <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden', background: bgColor }}>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        
        <DesktopSidebar 
          mosques={mosques} 
          selectedMosque={selectedMosque} 
          onSelectMosque={handleSelectMosque}
          isDark={isDark}
          onToggleTheme={() => setIsDark(!isDark)}
        />
        
        <div style={{ flex: 1, position: 'relative' }}>
          <MapContainer 
            center={[23.8103, 90.4125]}
            zoom={13} 
            zoomControl={true}
            style={{ height: '100%', width: '100%', background: isDark ? '#050505' : '#f8fafc' }}
          >
            <TileLayer 
              url={isDark 
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" 
                : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              } 
              attribution='&copy; OpenStreetMap, &copy; CartoDB' 
            />
            <MosqueAutoFit mosques={mosques} userLocation={userLocation} />
            <MapController onMoveEnd={handleMapMove} userLocation={userLocation} onMapDoubleClick={handleMapClick} />
            <UserLocationMarker userLocation={userLocation} />
            {mosques.map(mosque => (
              <MosqueMarker 
                key={mosque.id} 
                mosque={mosque} 
                onVote={handleVote} 
                isDark={isDark}
                onSelect={handleSelectMosque}
              />
            ))}
          </MapContainer>
          
          <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 1000, display: 'flex', gap: 8 }}>
            <button 
              onClick={() => setIsDark(!isDark)}
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: isDark ? 'rgba(30, 30, 30, 0.9)' : 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span style={{ fontSize: 20 }}>{isDark ? '☀️' : '🌙'}</span>
            </button>
            <button 
              onClick={requestLocation}
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span style={{ fontSize: 20 }}>📍</span>
            </button>
          </div>
          
          {(loading || osmLoading) && (
            <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '12px 24px', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
              <span style={{ color: '#1f2937', fontWeight: 600 }}>
                {osmLoading ? 'আশেপাশের মসজিদ খুঁজছি...' : 'লোড হচ্ছে...'}
              </span>
            </div>
          )}
          {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
            <div style={{ position: 'absolute', bottom: 20, right: 20, zIndex: 1000 }}>
              <div id="turnstile-widget-root"></div>
            </div>
          )}
          {feedError && !loading && (
            <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 10, zIndex: 1000, fontSize: 12, fontWeight: 600 }}>
              {feedError}
            </div>
          )}
        </div>
        
        {showReportModal && selectedMosque && (
          <ReportModal mosque={selectedMosque} date={today} onClose={() => setShowReportModal(false)} onSubmit={handleReportSubmit} isDark={isDark} />
        )}
        {showAddMosqueModal && addMosqueLocation && (
          <AddMosqueModal
            lat={addMosqueLocation.lat}
            lng={addMosqueLocation.lng}
            onClose={() => { setShowAddMosqueModal(false); setAddMosqueLocation(null); }}
            onSubmit={handleAddMosqueSubmit}
            isDark={isDark}
          />
        )}
        {/* Hint: double-click map to add mosque */}
        <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, pointerEvents: 'none' }}>
          <div style={{ background: isDark ? 'rgba(30,30,30,0.85)' : 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)', padding: '6px 14px', borderRadius: 99, fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500, whiteSpace: 'nowrap' }}>
            🕌 মসজিদ নেই? Map-এ double-click করুন
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="app">
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap" rel="stylesheet" />
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { font-family: 'Plus Jakarta Sans', sans-serif; }
        .map-bg {
          background-color: ${isDark ? '#050505' : '#f0f4f8'};
          background-image: ${isDark 
            ? 'radial-gradient(circle at 50% 50%, #0a1f12 0%, #050505 100%), linear-gradient(#1a1a1a 1px, transparent 1px), linear-gradient(90deg, #1a1a1a 1px, transparent 1px)'
            : 'radial-gradient(circle at 50% 50%, #ffffff 0%, #f0f4f8 100%), linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)'};
          background-size: 100% 100%, 40px 40px, 40px 40px;
        }
        .glass-panel {
          background: ${glassPanel};
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: ${cardBorder};
        }
        .marker-pulse { animation: pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite; }
        @keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 0.8; } 50% { opacity: 0.4; } 100% { transform: scale(2.2); opacity: 0; } }
        .leaflet-popup-content-wrapper { border-radius: 16px !important; box-shadow: 0 10px 40px rgba(0,0,0,0.15) !important; }
        .leaflet-popup-content { margin: 12px !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      
      <div className="map-bg" style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: isDark ? 0.1 : 0.3 }}>
          <path d="M-100,200 Q150,250 400,100 T900,300" fill="none" stroke={isDark ? '#333' : '#cbd5e1'} strokeWidth="8" />
          <path d="M50,800 Q300,600 500,800 T900,700" fill="none" stroke={isDark ? '#333' : '#cbd5e1'} strokeWidth="6" />
          <path d="M200,-50 L300,900" fill="none" stroke={isDark ? '#222' : '#94a3b8'} strokeWidth="12" />
          <path d="M600,-50 L550,900" fill="none" stroke={isDark ? '#222' : '#94a3b8'} strokeWidth="10" />
        </svg>
      </div>
      
      <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', height: '100%', pointerEvents: 'none' }}>
        <div style={{ paddingTop: '56px', paddingLeft: 16, paddingRight: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'auto' }}>
          <div className="glass-panel" style={{ borderRadius: '9999px', padding: '6px 6px 6px 20px', display: 'flex', alignItems: 'center', gap: 12, maxWidth: 320, width: '100%', boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              <span className="material-symbols-outlined" style={{ color: 'white', fontSize: 22 }}>radar</span>
            </div>
            <div>
              <h1 style={{ color: textColor, fontSize: 18, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.2 }}>বিরিয়ানি দিবে</h1>
              <span style={{ color: '#10b981', fontSize: 10, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>Live Tracker</span>
            </div>
          </div>
          
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: '9999px', background: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.1)' }}>
            <span className="material-symbols-outlined" style={{ color: '#f59e0b', fontSize: 14 }}>verified</span>
            <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>Created by <span style={{ color: textColor, fontWeight: 600 }}>DigitalCare.site</span></span>
          </div>
          {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
            <div id="turnstile-widget-root" style={{ marginTop: 10 }}></div>
          )}
        </div>
        
        <div style={{ flex: 1 }}></div>
        
        <div style={{ width: '100%', paddingBottom: 16, pointerEvents: 'auto' }}>
          <div style={{ paddingLeft: 20, paddingRight: 20, display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 16 }}>
            <button 
              onClick={() => setIsDark(!isDark)}
              className="glass-panel" 
              style={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                border: 'none', 
                cursor: 'pointer'
              }}
            >
              <span className="material-symbols-outlined" style={{ color: isDark ? '#fbbf24' : '#6366f1' }}>{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <button 
              onClick={requestLocation}
              className="glass-panel" 
              style={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                border: 'none', 
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              <span className="material-symbols-outlined" style={{ color: userLocation ? '#10b981' : textColor }}>my_location</span>
              {userLocation && (
                <div style={{ position: 'absolute', bottom: 2, right: 2, width: 8, height: 8, borderRadius: '50%', background: '#10b981', border: '2px solid white' }}></div>
              )}
            </button>
            <button className="glass-panel" style={{ width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', position: 'relative' }}>
              <span className="material-symbols-outlined" style={{ color: textColor }}>notifications</span>
              <div style={{ position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: '50%', background: '#ef4444', border: `2px solid ${isDark ? '#0f0f0f' : 'white'}` }}></div>
            </button>
          </div>
          
          {locationError && (
            <div style={{ paddingLeft: 16, paddingRight: 16, marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="material-symbols-outlined" style={{ color: '#ef4444', fontSize: 18 }}>location_off</span>
                <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 500 }}>{locationError}</span>
              </div>
            </div>
          )}
          {feedError && !loading && (
            <div style={{ paddingLeft: 16, paddingRight: 16, marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="material-symbols-outlined" style={{ color: '#ef4444', fontSize: 18 }}>error</span>
                <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 500 }}>{feedError}</span>
              </div>
            </div>
          )}
          
          <div style={{ paddingLeft: 16, paddingRight: 16 }}>
            <div className="glass-panel" style={{ borderRadius: 24, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -80, right: -80, width: 160, height: 160, borderRadius: '50%', background: 'rgba(16, 185, 129, 0.1)', filter: 'blur(40px)', pointerEvents: 'none' }}></div>
              <div style={{ width: 12, height: 4, background: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)', borderRadius: 2, margin: '0 auto 24px' }}></div>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>Nearby Status</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <h2 style={{ fontSize: 28, fontWeight: 800, color: textColor }}>{mosques.length} Mosques</h2>
                    <span style={{ fontSize: 14, color: '#10b981', fontWeight: 600 }}>Serving now</span>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    if (selectedMosque) {
                      handleReport(selectedMosque);
                    } else if (mosques.length > 0) {
                      // কোনো mosque select না থাকলে প্রথমটা দিয়ে modal খোলো
                      handleReport(mosques[0]);
                    }
                  }}
                  style={{ borderRadius: '9999px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', height: 56, paddingLeft: 20, paddingRight: 24, display: 'flex', alignItems: 'center', gap: 8, border: 'none', boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)', cursor: 'pointer' }}
                >
                  <span className="material-symbols-outlined" style={{ color: 'white', fontSize: 24, fontWeight: 700 }}>add_location_alt</span>
                  <span style={{ color: 'white', fontWeight: 700, fontSize: 14 }}>Report</span>
                </button>
              </div>
              
              <div style={{ display: 'flex', gap: 12, marginTop: 20, overflowX: 'auto' }}>
                <button style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: '9999px', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, color: textColor, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <span className="material-symbols-outlined" style={{ color: '#f59e0b', fontSize: 16 }}>star</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Premium</span>
                </button>
                <button style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: '9999px', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, color: textColor, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <span className="material-symbols-outlined" style={{ color: '#10b981', fontSize: 16 }}>timelapse</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Just Now</span>
                </button>
                <button style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: '9999px', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, color: isDark ? '#94a3b8' : '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>near_me</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Within 1km</span>
                </button>
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 8 }}>
            <a style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: '9999px', border: '1px solid rgba(245, 158, 11, 0.4)', background: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.2)', textDecoration: 'none' }} href="#">
              <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 300, letterSpacing: '2px', textTransform: 'uppercase' }}>Sponsored by</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: textColor, letterSpacing: '0.5px' }}>Ozzyl.com</span>
            </a>
          </div>
        </div>
        
        <div className="glass-panel" style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)', padding: '12px 24px 24px', display: 'flex', justifyContent: 'space-around', pointerEvents: 'auto' }}>
          <button
            onClick={() => setShowListSheet(false)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: !showListSheet ? '#10b981' : (isDark ? '#64748b' : '#94a3b8') }}
          >
            <div style={{ width: 48, height: 32, borderRadius: '9999px', background: !showListSheet ? 'rgba(16, 185, 129, 0.1)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>map</span>
            </div>
            <span style={{ fontSize: 11, fontWeight: !showListSheet ? 700 : 500 }}>Explore</span>
          </button>
          <button
            onClick={() => setShowListSheet(true)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: showListSheet ? '#10b981' : (isDark ? '#64748b' : '#94a3b8'), position: 'relative' }}
          >
            <div style={{ width: 48, height: 32, borderRadius: '9999px', background: showListSheet ? 'rgba(16, 185, 129, 0.1)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>list</span>
            </div>
            <span style={{ fontSize: 11, fontWeight: showListSheet ? 700 : 500 }}>List</span>
            {mosques.some(m => m.today?.score > 0) && (
              <div style={{ position: 'absolute', top: 2, right: 8, width: 8, height: 8, borderRadius: '50%', background: '#10b981', border: `2px solid ${isDark ? '#0f0f0f' : 'white'}` }} />
            )}
          </button>
          <button
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8' }}
          >
            <div style={{ width: 48, height: 32, borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>person</span>
            </div>
            <span style={{ fontSize: 11, fontWeight: 500 }}>Profile</span>
          </button>
        </div>
      </div>
      
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 5 }}>
        <MapContainer 
          center={[23.8103, 90.4125]}
          zoom={13} 
          zoomControl={false} 
          style={{ height: '100%', width: '100%', background: isDark ? '#050505' : '#f8fafc' }}
        >
          <TileLayer 
            url={isDark 
              ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" 
              : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            } 
            attribution='&copy; OpenStreetMap, &copy; CartoDB' 
          />
          <MosqueAutoFit mosques={mosques} userLocation={userLocation} />
          <MapController onMoveEnd={handleMapMove} userLocation={userLocation} onMapDoubleClick={handleMapClick} />
          <UserLocationMarker userLocation={userLocation} />
          {mosques.map(mosque => (
            <MosqueMarker key={mosque.id} mosque={mosque} onVote={handleVote} isDark={isDark} onSelect={handleSelectMosque} />
          ))}
        </MapContainer>
      </div>
      
      {loading && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: isDark ? 'rgba(15, 25, 20, 0.95)' : 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', padding: '16px 32px', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 1000, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 24, height: 24, border: '3px solid rgba(16, 185, 129, 0.3)', borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <span style={{ color: textColor, fontWeight: 600 }}>লোড হচ্ছে...</span>
        </div>
      )}
      
      {showReportModal && selectedMosque && (
        <ReportModal mosque={selectedMosque} date={today} onClose={() => setShowReportModal(false)} onSubmit={handleReportSubmit} isDark={isDark} />
      )}
      {showAddMosqueModal && addMosqueLocation && (
        <AddMosqueModal
          lat={addMosqueLocation.lat}
          lng={addMosqueLocation.lng}
          onClose={() => { setShowAddMosqueModal(false); setAddMosqueLocation(null); }}
          onSubmit={handleAddMosqueSubmit}
          isDark={isDark}
        />
      )}
      {/* Mosque list bottom sheet */}
      {showListSheet && (
        <MosqueListSheet
          mosques={mosques}
          userLocation={userLocation}
          onSelectMosque={(mosque) => {
            handleSelectMosque(mosque);
            setShowListSheet(false);
          }}
          onReportMosque={(mosque) => {
            handleReport(mosque);
            setShowListSheet(false);
          }}
          onClose={() => setShowListSheet(false)}
          isDark={isDark}
        />
      )}
      {/* Hint: double-click map to add mosque — hidden when list sheet open */}
      {!showListSheet && (
        <div style={{ position: 'fixed', bottom: 110, left: '50%', transform: 'translateX(-50%)', zIndex: 20, pointerEvents: 'none' }}>
          <div style={{ background: isDark ? 'rgba(30,30,30,0.85)' : 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)', padding: '6px 14px', borderRadius: 99, fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500, whiteSpace: 'nowrap' }}>
            🕌 মসজিদ নেই? Map-এ double-click করুন
          </div>
        </div>
      )}
    </div>
  );
}
